import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import useAppNavigate from "hooks/useAppNavigate";
import type {
  ClientPortalIntegrationItem,
  ClientPortalIntegrationSortField,
} from "features/integrationsBeta/integrationsBeta";
import type { TimezoneDisplayMode } from "features/integrationsBeta/timezoneDisplay";
import CustomScrollbars from "../CustomScrollbars";
import ClientRunStatusChip from "./ClientRunStatusChip";
import {
  EnabledCell,
  FrequencyCell,
  IntegrationNameCell,
  TimestampCell,
  timeColumnLabel,
} from "./integrationListCells";
import TableSkeletonRows from "./TableSkeletonRows";
import TypeChipList from "./TypeChipList";
import { HEADER_CELL_SX, NO_WRAP_CELL_SX } from "./tableStyles";

interface Column {
  key: string;
  label: string;
  /** Fixed proportional width (table-layout: fixed); long content truncates. */
  width: string;
  sortField?: ClientPortalIntegrationSortField;
}

// The legacy client dashboard's columns, with the client-facing status in
// place of the engine's (CAL-5777). No Client column: a client sees one.
const COLUMNS: Column[] = [
  { key: "name", label: "Integration", width: "30%", sortField: "name" },
  { key: "source_services", label: "Source", width: "7%" },
  { key: "target_services", label: "Target", width: "7%" },
  { key: "enabled", label: "Enabled", width: "7%", sortField: "enabled" },
  { key: "frequency", label: "Frequency", width: "17%" },
  {
    key: "last_run_started_at",
    label: "Last Run",
    width: "12%",
    sortField: "last_run_started_at",
  },
  { key: "next_run_at", label: "Next Run", width: "12%" },
  {
    key: "last_run_client_status",
    label: "Status",
    width: "8%",
    sortField: "last_run_client_status",
  },
];

type Props = {
  integrations: ClientPortalIntegrationItem[];
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  sortBy?: ClientPortalIntegrationSortField;
  sortOrder: "asc" | "desc";
  onSortChange: (field: ClientPortalIntegrationSortField) => void;
  timezoneMode: TimezoneDisplayMode;
  /** Labels of GET /runs/client-statuses/. */
  clientStatusLabel: (value: string | null | undefined) => string;
  pagination?: React.ReactNode;
};

/**
 * The integrations of ONE client as the client sees them: the client-facing
 * status of the latest run and nothing internal. Rendered for a client portal
 * user and for an admin previewing one in Client View.
 */
export default function ClientIntegrationsTable({
  integrations,
  isLoading,
  isFetching,
  error,
  sortBy,
  sortOrder,
  onSortChange,
  timezoneMode,
  clientStatusLabel,
  pagination,
}: Props) {
  const navigate = useAppNavigate();

  if (error) {
    return (
      <Typography p={2} variant="h6" align="center" color="error">
        Error loading integrations
      </Typography>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "clip",
      }}
    >
      <CustomScrollbars sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1200,
            width: "100%",
            tableLayout: "fixed",
            borderColor: "divider",
          }}
        >
          <colgroup>
            {COLUMNS.map((col) => (
              <col key={col.key} style={{ width: col.width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{ ...HEADER_CELL_SX, whiteSpace: "nowrap" }}
                >
                  {col.sortField ? (
                    <TableSortLabel
                      active={sortBy === col.sortField}
                      direction={sortBy === col.sortField ? sortOrder : "asc"}
                      onClick={() => onSortChange(col.sortField!)}
                    >
                      {timeColumnLabel(col.key, col.label, timezoneMode)}
                    </TableSortLabel>
                  ) : (
                    timeColumnLabel(col.key, col.label, timezoneMode)
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={COLUMNS.length} rows={8} />
            ) : integrations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography color="text.secondary">
                    No integrations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              integrations.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => navigate(`/integrations-beta/${row.id}`)}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: "none",
                    },
                    opacity: isFetching ? 0.6 : 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "calibrate.primarySoft06" },
                  }}
                >
                  <TableCell sx={NO_WRAP_CELL_SX} title={row.name}>
                    <IntegrationNameCell
                      name={row.name}
                      timezone={row.timezone}
                    />
                  </TableCell>
                  <TableCell>
                    <TypeChipList items={row.source_services} />
                  </TableCell>
                  <TableCell>
                    <TypeChipList items={row.target_services} />
                  </TableCell>
                  <TableCell>
                    <EnabledCell enabled={row.enabled} />
                  </TableCell>
                  <TableCell>
                    <FrequencyCell
                      frequency={row.frequency}
                      crons={row.active_schedule_crons}
                      timezone={row.timezone}
                      timezoneMode={timezoneMode}
                    />
                  </TableCell>
                  <TableCell sx={NO_WRAP_CELL_SX}>
                    <TimestampCell
                      iso={row.last_run_started_at}
                      timezone={row.timezone}
                      timezoneMode={timezoneMode}
                    />
                  </TableCell>
                  <TableCell sx={NO_WRAP_CELL_SX}>
                    <TimestampCell
                      iso={row.next_run_at}
                      timezone={row.timezone}
                      timezoneMode={timezoneMode}
                      color="text.secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <ClientRunStatusChip
                      status={row.last_run_client_status}
                      label={clientStatusLabel(row.last_run_client_status)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Paper>
  );
}
